import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { MessageCircle, X, Send, ArrowLeft, Smartphone, Paperclip, FileText } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import {
  supportChatApi, formatFileSize, attachmentFailureMessage,
  sendAttachmentBatch, AttachmentBatchError, formatBatchProgress,
} from '@/services/supportChat';
import type { AttachmentBatchProgress, SupportConversation, SupportMessage } from '@/services/supportChat';
import FileAttachment from '@/components/support-chat/FileAttachment';
import LinkifiedText from '@/components/support-chat/LinkifiedText';
import { useFileAttachment } from '@/components/support-chat/useFileAttachment';
import { OPEN_SUPPORT_CHAT_EVENT, SUPPORT_CHAT_QUERY_PARAM } from '@/components/support-chat/openSupportChat';

type WidgetState = 'closed' | 'welcome' | 'chatting';

function getAnonymousId(): string {
  let id = localStorage.getItem('support_anonymous_id');
  if (!id) {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    id = `anon_${hex}`;
    localStorage.setItem('support_anonymous_id', id);
  }
  return id;
}

function formatTime(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleTimeString('ko-KR', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Seoul',
  });
}

// 403/404 mean the saved conversation is not this visitor's (logged in or out
// since) or no longer exists; anything else is a transient failure.
function isConversationGoneError(error: unknown): boolean {
  const status = (error as { response?: { status?: number } })?.response?.status;
  return status === 403 || status === 404;
}

// Polling only replaces state when something visible changed, including
// read receipts, which change no message count.
function areMessagesEqual(a: SupportMessage[], b: SupportMessage[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((x, i) => {
    const y = b[i];
    return x.id === y.id && x.content === y.content && x.read_at === y.read_at;
  });
}

function isValidKoreanPhone(phone: string): boolean {
  const cleaned = phone.replace(/\D/g, '');
  return /^01[016789]\d{7,8}$/.test(cleaned);
}

// Welcome message shown automatically when chat starts
const WELCOME_MESSAGE: SupportMessage = {
  id: -1,
  conversation_id: 0,
  sender_type: 'admin',
  sender_id: null,
  content: '안녕하세요! AutoQM 고객센터입니다. 궁금한 점이 있으시면 편하게 문의해 주세요.',
  message_type: 'text',
  created_at: new Date().toISOString(),
};

// Thin wrapper that gates rendering on non-admin pages.
// The admin check lives here (with only useLocation as a hook) so that
// SupportChatWidgetInner is never mounted on admin pages and esbuild
// cannot reorder its hooks around an early return.
export default function SupportChatWidget() {
  const location = useLocation();
  if (location.pathname.startsWith('/admin')) return null;
  const shouldAvoidMobileBottomBar = /^\/store\/\d+$/.test(location.pathname);
  return <SupportChatWidgetInner shouldAvoidMobileBottomBar={shouldAvoidMobileBottomBar} />;
}

function SupportChatWidgetInner({ shouldAvoidMobileBottomBar = false }: { shouldAvoidMobileBottomBar?: boolean }) {
  const { state: authState } = useAuth();
  const { user } = authState;
  const mobileBottomClass = shouldAvoidMobileBottomBar ? 'bottom-28 lg:bottom-5' : 'bottom-5';
  const chatMaxHeightClass = shouldAvoidMobileBottomBar
    ? 'max-h-[calc(100vh-9rem)] lg:max-h-[calc(100vh-40px)]'
    : 'max-h-[calc(100vh-40px)]';

  const [widgetState, setWidgetState] = useState<WidgetState>('closed');
  const [conversation, setConversation] = useState<SupportConversation | null>(null);
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [connectionError, setConnectionError] = useState(false);

  // SMS inline form state (shown inside chat area)
  const [showSmsForm, setShowSmsForm] = useState(false);
  const [smsPhone, setSmsPhone] = useState('');
  const [smsMessage, setSmsMessage] = useState('');
  const [smsSubmitting, setSmsSubmitting] = useState(false);
  const [smsConfirmedPhone, setSmsConfirmedPhone] = useState('');
  const [useCustomPhone, setUseCustomPhone] = useState(false);

  const {
    pendingFiles, uploading, setUploading,
    isDragging, fileInputRef, addFiles, removePendingFile, clearPendingFiles, restorePendingFiles,
    handlePaste, handleDragEnter, handleDragOver, handleDragLeave, handleDrop,
  } = useFileAttachment({ disabled: sending });
  const [uploadError, setUploadError] = useState('');
  const [batchProgress, setBatchProgress] = useState<AttachmentBatchProgress | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sendingRef = useRef(false);
  const startingChatRef = useRef(false);
  const messageMutationRevisionRef = useRef(0);
  // Set by ?support_chat=open before the saved conversation finishes restoring.
  const openWhenRestoredRef = useRef(false);
  const smsFormRef = useRef<HTMLDivElement>(null);
  const anonymousId = !user ? getAnonymousId() : undefined;

  // Determine if user has a registered phone
  const userPhone = user?.phone || '';
  const hasRegisteredPhone = !!userPhone && !useCustomPhone;

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const forgetConversation = useCallback(() => {
    localStorage.removeItem('support_conversation_id');
    setConversation(null);
    setMessages([]);
    setUnreadCount(0);
    setConnectionError(false);
    setWidgetState((state) => (state === 'chatting' ? 'welcome' : state));
  }, []);

  // Poll for new messages when chatting
  useEffect(() => {
    if (widgetState !== 'chatting' || !conversation) return;

    let lastFetchedCount = -1;

    const fetchMessages = async () => {
      if (sendingRef.current) return;
      const mutationRevision = messageMutationRevisionRef.current;

      try {
        const res = await supportChatApi.getMessages(conversation.id, anonymousId);
        if (
          sendingRef.current
          || mutationRevision !== messageMutationRevisionRef.current
        ) return;

        const newMessages = res.data;
        setConnectionError(false);
        setMessages((prev) => (areMessagesEqual(prev, newMessages) ? prev : newMessages));
        // Only a visible chat counts as read (a background tab still polls).
        if (newMessages.length !== lastFetchedCount && document.visibilityState === 'visible') {
          lastFetchedCount = newMessages.length;
          setUnreadCount(0);
          supportChatApi.markAsRead(conversation.id, {
            role: 'user',
            anonymous_id: anonymousId,
          }).catch(() => {});
        }
      } catch (error) {
        if (isConversationGoneError(error)) {
          forgetConversation();
          return;
        }
        setConnectionError(true);
      }
    };

    fetchMessages();
    pollIntervalRef.current = setInterval(fetchMessages, 3000);

    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, [anonymousId, conversation, widgetState, forgetConversation]);

  // Poll for unread count when widget is closed
  useEffect(() => {
    if (widgetState !== 'closed' || !conversation) return;

    const checkUnread = async () => {
      if (document.visibilityState === 'hidden') return;

      try {
        const res = await supportChatApi.getConversationUnreadCount(
          conversation.id,
          anonymousId
        );
        setUnreadCount(res.data.count);
      } catch (error) {
        if (isConversationGoneError(error)) forgetConversation();
      }
    };

    checkUnread();
    const interval = setInterval(checkUnread, 10000);
    return () => clearInterval(interval);
  }, [anonymousId, conversation, widgetState, forgetConversation]);

  // Restore conversation from localStorage. Re-runs when the visitor logs in
  // or out, dropping a conversation that belongs to the previous identity.
  useEffect(() => {
    let canceled = false;
    const conversationId = parseInt(localStorage.getItem('support_conversation_id') ?? '', 10);
    if (!Number.isInteger(conversationId) || conversationId <= 0) {
      forgetConversation();
      return;
    }
    supportChatApi.getConversationUnreadCount(conversationId, anonymousId)
      .then((res) => {
        if (canceled) return;
        setConversation((prev) => (
          prev?.id === conversationId ? prev : ({ id: conversationId } as SupportConversation)
        ));
        setUnreadCount(res.data.count);
        if (openWhenRestoredRef.current) {
          openWhenRestoredRef.current = false;
          setWidgetState((state) => (state === 'welcome' ? 'chatting' : state));
        }
      })
      .catch((error) => {
        if (canceled) return;
        if (isConversationGoneError(error)) {
          forgetConversation();
        } else {
          // Transient failure: keep the saved conversation; polling retries.
          setConversation((prev) => prev ?? ({ id: conversationId } as SupportConversation));
        }
      });
    return () => { canceled = true; };
  }, [anonymousId, forgetConversation]);

  // Scroll on new messages
  useEffect(() => {
    scrollToBottom();
  }, [messages.length, scrollToBottom]);

  // Allow pages to open the widget (e.g. "실시간 상담으로 문의하기" buttons)
  useEffect(() => {
    const handleOpenRequest = () => {
      setWidgetState(conversation ? 'chatting' : 'welcome');
    };
    window.addEventListener(OPEN_SUPPORT_CHAT_EVENT, handleOpenRequest);
    return () => window.removeEventListener(OPEN_SUPPORT_CHAT_EVENT, handleOpenRequest);
  }, [conversation]);

  // Links from outside the page (e.g. the desktop app's 실시간 상담 menu)
  // open the widget with ?support_chat=open; the parameter is then removed
  // so a reload or shared URL does not reopen it.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get(SUPPORT_CHAT_QUERY_PARAM) !== 'open') return;
    if (!conversation) openWhenRestoredRef.current = true;
    setWidgetState((state) => (state === 'closed' ? (conversation ? 'chatting' : 'welcome') : state));
    const next = new URLSearchParams(searchParams);
    next.delete(SUPPORT_CHAT_QUERY_PARAM);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, conversation]);

  // Scroll to SMS form when it opens
  useEffect(() => {
    if (showSmsForm && smsFormRef.current) {
      smsFormRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [showSmsForm]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  }, [input]);

  const handleStartChat = async () => {
    if (startingChatRef.current) return;
    startingChatRef.current = true;
    try {
      const res = await supportChatApi.createOrGetConversation({
        anonymous_id: anonymousId,
      });
      setConversation(res.data);
      localStorage.setItem('support_conversation_id', String(res.data.id));
      setWidgetState('chatting');
    } catch {
      // ignore
    } finally {
      startingChatRef.current = false;
    }
  };

  const handleSendMessage = async () => {
    if (
      (!input.trim() && pendingFiles.length === 0)
      || !conversation
      || sending
      || sendingRef.current
      || uploading
    ) return;

    const content = input.trim();
    const files = pendingFiles.map((item) => item.file);
    sendingRef.current = true;
    messageMutationRevisionRef.current += 1;
    setInput('');
    clearPendingFiles();
    setSending(true);
    setUploadError('');

    try {
      if (files.length > 0) {
        // Each file goes as its own message, one after another
        // (direct-to-R2 with legacy multipart fallback).
        setUploading(true);
        await sendAttachmentBatch({
          conversationId: conversation.id,
          files,
          senderType: 'user',
          anonymousId,
          content,
          onProgress: setBatchProgress,
          onSent: (message) => {
            messageMutationRevisionRef.current += 1;
            setMessages((prev) => [...prev, message]);
          },
        });
      } else {
        // Optimistic update for text-only
        const optimisticMsg: SupportMessage = {
          id: Date.now(),
          conversation_id: conversation.id,
          sender_type: 'user',
          sender_id: user?.id ?? null,
          content,
          message_type: 'text',
          created_at: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, optimisticMsg]);

        try {
          await supportChatApi.sendMessage(conversation.id, {
            content,
            sender_type: 'user',
            anonymous_id: anonymousId,
          });
        } catch {
          setMessages((prev) => prev.filter((m) => m.id !== optimisticMsg.id));
          setInput(content);
        }
      }
    } catch (err) {
      const failure = err instanceof AttachmentBatchError ? err : null;
      restorePendingFiles(failure ? failure.remaining : files);
      const cause = failure ? failure.originalError : err;
      const reason = attachmentFailureMessage(cause);
      setUploadError(failure && failure.sentCount > 0
        ? `${failure.sentCount}개는 보냈고 ${failure.remaining.length}개가 남았습니다. ${reason}`
        : reason);
      if (content && (!failure || failure.contentUnsent)) setInput(content);
    } finally {
      sendingRef.current = false;
      setSending(false);
      setUploading(false);
      setBatchProgress(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // A Korean IME sends Enter twice when the last syllable is still composing;
    // sending on the first one leaves that syllable behind in the box.
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleClose = () => {
    setWidgetState('closed');
    setShowSmsForm(false);
    setSmsConfirmedPhone('');
    clearPendingFiles();
    const adminMsgCount = messages.filter(
      (m) => m.sender_type === 'admin' || m.sender_type === 'system'
    ).length;
    localStorage.setItem('support_last_seen_count', String(adminMsgCount));
  };

  const handleSmsRequest = async () => {
    if (!conversation || smsSubmitting) return;

    const phone = hasRegisteredPhone ? userPhone : smsPhone;
    if (!isValidKoreanPhone(phone)) return;

    setSmsSubmitting(true);
    try {
      await supportChatApi.requestSmsReply(conversation.id, {
        phone: phone.replace(/\D/g, ''),
        message: smsMessage.trim() || undefined,
        anonymous_id: anonymousId,
      });
      setSmsConfirmedPhone(phone.replace(/\D/g, ''));
      setShowSmsForm(false);
      setSmsPhone('');
      setSmsMessage('');
      setUseCustomPhone(false);
    } catch {
      // ignore
    } finally {
      setSmsSubmitting(false);
    }
  };

  if (widgetState === 'closed') {
    return (
      <button
        onClick={() => setWidgetState(conversation ? 'chatting' : 'welcome')}
        className={`fixed ${mobileBottomClass} right-5 z-50 flex items-center justify-center w-14 h-14 rounded-full bg-amber-500 text-white shadow-lg hover:bg-amber-600 transition-all hover:scale-105 active:scale-95`}
        aria-label="고객 문의"
      >
        <MessageCircle className="w-6 h-6" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>
    );
  }

  if (widgetState === 'welcome') {
    return (
      <div className={`fixed ${mobileBottomClass} right-5 z-50 w-[360px] max-w-[calc(100vw-40px)] rounded-2xl shadow-2xl overflow-hidden bg-white border border-gray-200 sm:w-[360px]`}>
        <div className="bg-amber-500 px-5 py-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center text-lg font-bold">
                Q
              </div>
              <div>
                <div className="font-bold text-base">AutoQM</div>
                <div className="text-amber-100 text-xs">고객 상담</div>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-1 rounded-full hover:bg-white/20 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-sm text-amber-50 leading-relaxed">
            안녕하세요! 궁금한 점이 있으시면 편하게 문의해 주세요.
          </p>
        </div>
        <div className="p-5">
          <div className="flex items-center gap-2 text-xs text-gray-500 mb-4">
            <span className="w-2 h-2 bg-green-400 rounded-full" />
            평균 응답 시간: 24시간 이내
          </div>
          <button
            onClick={handleStartChat}
            className="w-full py-3 bg-amber-500 text-white rounded-xl font-semibold hover:bg-amber-600 transition-colors text-sm"
          >
            문의하기
          </button>
        </div>
      </div>
    );
  }

  // --- CHATTING STATE ---
  const displayMessages = messages.length <= 1
    ? [WELCOME_MESSAGE, ...messages]
    : messages;

  const effectivePhone = hasRegisteredPhone ? userPhone : smsPhone;
  const canSubmitSms = isValidKoreanPhone(effectivePhone) && !smsSubmitting;

  return (
    <div
      className={`fixed ${mobileBottomClass} right-5 z-50 w-[380px] max-w-[calc(100vw-40px)] h-[520px] ${chatMaxHeightClass} rounded-2xl shadow-2xl overflow-hidden bg-white border border-gray-200 flex flex-col sm:w-[380px] relative`}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {isDragging && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/90 border-4 border-dashed border-amber-400 pointer-events-none">
          <p className="text-sm font-medium text-amber-600 text-center px-4">여기에 파일을 놓으면 첨부됩니다</p>
        </div>
      )}
      {/* Header */}
      <div className="bg-amber-500 px-4 py-3 text-white flex items-center gap-3 shrink-0">
        <button
          onClick={() => setWidgetState('welcome')}
          className="p-1 rounded-full hover:bg-white/20 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-2 flex-1">
          <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center text-sm font-bold">
            Q
          </div>
          <div>
            <div className="font-semibold text-sm">AutoQM 고객 상담</div>
            <div className="text-amber-100 text-[11px]">보통 24시간 이내 답변</div>
          </div>
        </div>
        <button
          onClick={handleClose}
          className="p-1 rounded-full hover:bg-white/20 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Connection error banner */}
      {connectionError && (
        <div className="px-4 py-2 bg-red-50 text-red-600 text-xs text-center shrink-0">
          연결이 불안정합니다. 자동으로 재시도 중...
        </div>
      )}

      {/* Messages + inline SMS form */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
        {displayMessages.map((msg) => {
          if (msg.sender_type === 'system') {
            return (
              <div key={msg.id} className="flex justify-center">
                <span className="text-[11px] text-gray-400 bg-gray-100 px-3 py-1 rounded-full">
                  {msg.content}
                </span>
              </div>
            );
          }

          const isUser = msg.sender_type === 'user';
          return (
            <div key={msg.id} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
              <div className={`min-w-0 max-w-[75%] ${isUser ? 'order-2' : ''}`}>
                {!isUser && (
                  <div className="text-[11px] text-gray-500 mb-1 ml-1">관리자</div>
                )}
                {msg.content && (
                  <div
                    className={`px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap break-words ${
                      isUser
                        ? 'bg-amber-500 text-white rounded-br-md'
                        : 'bg-white text-gray-800 rounded-bl-md border border-gray-200'
                    }`}
                  >
                    <LinkifiedText text={msg.content} />
                  </div>
                )}
                {msg.attachment_url && <FileAttachment msg={msg} anonymousId={anonymousId} maxImageClass="max-w-[200px] max-h-[200px]" overlayZClass="z-[200]" />}
                <div className={`text-[10px] text-gray-400 mt-0.5 ${isUser ? 'text-right mr-1' : 'ml-1'}`}>
                  {isUser && msg.read_at && (
                    <span className="text-amber-500 mr-1.5">읽음 · {formatTime(msg.read_at)}</span>
                  )}
                  {formatTime(msg.created_at)}
                </div>
              </div>
            </div>
          );
        })}

        {/* Inline SMS request form (shown inside chat flow) */}
        {showSmsForm && (
          <div ref={smsFormRef} className="bg-white border border-amber-200 rounded-2xl p-4 space-y-3 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Smartphone className="w-4 h-4 text-amber-500" />
                <span className="text-sm font-semibold text-gray-800">문자로 답변받기</span>
              </div>
              <button
                onClick={() => { setShowSmsForm(false); setUseCustomPhone(false); }}
                className="p-0.5 rounded-full hover:bg-gray-100 transition-colors"
              >
                <X className="w-3.5 h-3.5 text-gray-400" />
              </button>
            </div>

            <p className="text-xs text-gray-500">
              채팅창을 닫아도 답변이 준비되면 문자로 알려드릴게요.
            </p>

            {/* Phone number */}
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1.5">답변 받을 번호</label>
              {hasRegisteredPhone ? (
                <div className="flex items-center gap-2.5 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5">
                  <Smartphone className="w-4 h-4 text-amber-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900">{userPhone}</div>
                    <div className="text-[10px] text-gray-400">회원 등록 번호</div>
                  </div>
                  <button
                    onClick={() => setUseCustomPhone(true)}
                    className="text-[11px] text-amber-600 hover:text-amber-700 underline shrink-0"
                  >
                    변경
                  </button>
                </div>
              ) : (
                <div>
                  <input
                    type="tel"
                    value={smsPhone}
                    onChange={(e) => setSmsPhone(e.target.value.replace(/\D/g, '').slice(0, 11))}
                    placeholder="01012345678"
                    maxLength={11}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400 bg-gray-50"
                  />
                  <p className="text-[10px] text-gray-400 mt-1 ml-1">'-' 없이 숫자만 입력</p>
                </div>
              )}
            </div>

            {/* Additional message */}
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1.5">
                추가 문의사항 <span className="text-gray-400">(선택)</span>
              </label>
              <textarea
                value={smsMessage}
                onChange={(e) => setSmsMessage(e.target.value.slice(0, 500))}
                placeholder="더 궁금한 점이 있으면 남겨주세요..."
                rows={2}
                className="w-full resize-none border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400 bg-gray-50"
              />
            </div>

            {/* Submit */}
            <button
              onClick={handleSmsRequest}
              disabled={!canSubmitSms}
              className="w-full py-2.5 bg-amber-500 text-white rounded-xl font-semibold hover:bg-amber-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-sm"
            >
              {smsSubmitting ? '요청 중...' : '문자 답변 요청하기'}
            </button>

            <p className="text-[10px] text-gray-400 text-center">
              입력하신 번호는 답변 발송 목적으로만 사용됩니다.
            </p>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input area + SMS trigger icon */}
      <div className="border-t border-gray-200 bg-white shrink-0">
        {/* SMS trigger row (above input) */}
        {conversation && !showSmsForm && !smsConfirmedPhone && (
          <button
            onClick={() => setShowSmsForm(true)}
            className="flex items-center gap-1.5 px-4 pt-2 pb-0.5 text-left w-full group"
          >
            <Smartphone className="w-3.5 h-3.5 text-amber-500" />
            <span className="text-[11px] text-amber-600 group-hover:text-amber-700 group-hover:underline">
              문자로 답변받기
            </span>
          </button>
        )}

        {/* Queued files (sent one message each, in order) */}
        {pendingFiles.length > 0 && (
          <div className="px-3 pt-2 space-y-1.5 max-h-40 overflow-y-auto">
            {pendingFiles.map((item) => (
              <div key={item.id} className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl">
                {item.preview ? (
                  <img src={item.preview} alt="preview" className="w-10 h-10 rounded-lg object-cover shrink-0" />
                ) : (
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center shrink-0">
                    <FileText className="w-4 h-4 text-gray-400" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] font-medium text-gray-700 truncate">{item.file.name}</div>
                  <div className="text-[10px] text-gray-400">{formatFileSize(item.file.size)}</div>
                </div>
                <button
                  onClick={() => removePendingFile(item.id)}
                  className="p-1 rounded-full hover:bg-gray-200 transition-colors shrink-0"
                  aria-label={`${item.file.name} 첨부 취소`}
                >
                  <X className="w-3.5 h-3.5 text-gray-400" />
                </button>
              </div>
            ))}
          </div>
        )}

        {uploadError && (
          <div className="px-3 pt-1">
            <p className="text-[11px] text-red-500">{uploadError}</p>
          </div>
        )}

        <div className="p-3 pt-2">
          <div className="flex items-end gap-2">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => {
                const selected = Array.from(e.target.files ?? []);
                if (selected.length > 0) {
                  addFiles(selected);
                  setUploadError('');
                }
              }}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="p-2.5 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
              title="파일 첨부"
            >
              <Paperclip className="w-4 h-4" />
            </button>
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value.slice(0, 5000))}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              placeholder={pendingFiles.length > 0 ? '설명을 추가하세요...' : '메시지를 입력하세요...'}
              maxLength={5000}
              rows={1}
              className="flex-1 resize-none border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400 max-h-[120px] bg-gray-50"
            />
            <button
              onClick={handleSendMessage}
              disabled={(!input.trim() && pendingFiles.length === 0) || sending || uploading}
              className="p-2.5 rounded-xl bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
          {uploading && (
            <p className="text-[11px] text-amber-600 mt-1 ml-11">
              {batchProgress ? formatBatchProgress(batchProgress) : '파일 업로드 중...'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
